import styled from 'styled-components'

const FACE = `Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`

type RenewalContactBandProps = {
  page?: string
}

export default function RenewalContactBand({ page = 'home' }: RenewalContactBandProps) {
  return (
    <Band aria-label="문의 창구">
      <Inner>
        <Intro>
          <Title>궁금한 점이 있으신가요?</Title>
          <Desc>상황에 맞는 창구로 바로 연결돼요. 전화는 평일 10:00~18:00에 받아요.</Desc>
        </Intro>
        <Cards>
          <Card>
            <Kicker>신규 상담</Kicker>
            <CardTitle>처음 상담하시는 사장님</CardTitle>
            <Action href="tel:025080913" data-cta={`${page}_band_tel`}>
              <Bubble>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
                </svg>
              </Bubble>
              <PhoneCol>
                <PhoneNo>02-508-0913</PhoneNo>
                <PhoneHours>평일 10:00~18:00</PhoneHours>
              </PhoneCol>
            </Action>
            <Action href="https://brwnie.channel.io" target="_blank" rel="noopener" data-cta={`${page}_band_chat`}>
              <Bubble>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
                </svg>
              </Bubble>
              채팅으로 상담하기
            </Action>
          </Card>
          <Card>
            <Kicker>이용 중 문의</Kicker>
            <CardTitle>이용 중인 사장님</CardTitle>
            <Action href="https://pf.kakao.com/_aDlss/chat" target="_blank" rel="noopener" data-cta={`${page}_band_kakao`}>
              <Bubble>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 3C6.5 3 2 6.6 2 11c0 2.8 1.8 5.2 4.6 6.6L5.7 21l4-2.6c.7.1 1.5.2 2.3.2 5.5 0 10-3.6 10-8S17.5 3 12 3z" />
                </svg>
              </Bubble>
              카카오톡 채널
            </Action>
          </Card>
          <Card>
            <Kicker>본사 · 제휴</Kicker>
            <CardTitle>프랜차이즈 본사·제휴 문의</CardTitle>
            <Mail href="mailto:partnership@higherx.co.kr" data-cta={`${page}_band_mail`}>
              <Bubble>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
              </Bubble>
              partnership@higherx.co.kr
            </Mail>
          </Card>
        </Cards>
      </Inner>
    </Band>
  )
}

const Band = styled.aside`
  background: #ffffff;
  border-top: 1px solid #e5e8eb;
  font-family: ${FACE};
  color: #191f28;
  word-break: keep-all;
  -webkit-font-smoothing: antialiased;
`

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(40px, 5vw, 64px) clamp(20px, 3.4vw, 40px);
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: clamp(24px, 3vw, 48px);
`

const Intro = styled.div`
  flex: 1 1 260px;
  min-width: 0;
`

const Title = styled.h2`
  margin: 0;
  font-family: ${FACE};
  font-size: clamp(22px, 2.2vw, 30px);
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.03em;
  color: #191f28;
`

const Desc = styled.p`
  margin: 10px 0 0;
  max-width: 36ch;
  font-family: ${FACE};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.65;
  color: #4e5968;
  text-wrap: pretty;
`

const Cards = styled.div`
  flex: 2.2 1 560px;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 264px), 1fr));
  gap: 12px;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 18px;
  background: #f2f4f6;
`

const Kicker = styled.p`
  margin: 0;
  font-family: ${FACE};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
  color: #3d5afe;
`

const CardTitle = styled.p`
  margin: 4px 0 10px;
  font-family: ${FACE};
  font-size: 17px;
  font-weight: 800;
  line-height: 1.6;
  letter-spacing: -0.02em;
  color: #191f28;
`

const Action = styled.a`
  && {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    color: #191f28;
    text-decoration: none;
    font-family: ${FACE};
    font-size: 15px;
    font-weight: 700;
    line-height: 1.6;
    transition: color 0.2s;
  }

  &:hover {
    color: #003399;
  }
`

const Mail = styled(Action)`
  && {
    white-space: nowrap;
  }
`

const Bubble = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  color: #003399;
  display: grid;
  place-items: center;
`

const PhoneCol = styled.span`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
`

const PhoneNo = styled.span`
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
  color: #191f28;
`

const PhoneHours = styled.span`
  font-family: ${FACE};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: #4e5968;
`
